import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { getGameRoster } from '@sportninja/common/services/game/rosterServices'
import type {
  GameRosterOfficial,
  GameRosterPlayer,
  GameRosterResponse,
} from '../types'

/**
 * Short, so the page picks up attendance / confirmation changes made elsewhere
 * (the attendance page, the app) without a reload — together with the refetch
 * when the tab regains focus.
 */
const STALE_TIME = 15 * 1000

interface Result {
  players: GameRosterPlayer[]
  officials: GameRosterOfficial[]
  isLoading: boolean
  error: string | null
}

/** One team's roster for a game: its players and team officials. */
export const useGameRoster = (gameId?: string, rosterId?: string): Result => {
  const query = useQuery({
    queryKey: queryKeys.gameRoster.byRoster(gameId, rosterId),
    queryFn: () =>
      getGameRoster(
        gameId as string,
        rosterId as string
      ) as Promise<GameRosterResponse>,
    enabled: Boolean(gameId && rosterId),
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
  })

  return {
    players: query.data?.players ?? [],
    officials: query.data?.team_officials ?? [],
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  }
}
